import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FeatureGuard from '../components/common/FeatureGuard';
import { useDecodedToken } from '../utils/useDecodedToken';
import UserTypeIndicator from '../components/common/UserTypeIndicator';
import { api } from '../api/client';
import { PDFDownloadLink } from '@react-pdf/renderer';
import StatementPdf from '../components/statement/StatementPdf';
import * as ExcelJS from 'exceljs';

interface Transaction {
  txnDate: string;
  voucher: string;
  narration: string;
  debit: number;
  credit: number;
  balance: number;
}

interface ROIHistory {
  sno: number;
  startDate: string;
  endDate: string;
  roi: string;
  emiAmount: number;
}

interface Statement {
  id: number;
  accountNo: string;
  loanAccountNo?: string;
  accountType: string;
  productName?: string;
  shortProductName?: string;
  period: string;
  startDate: string;
  endDate: string;
  openingBalance: number;
  closingBalance: number;
  totalCredits: number;
  totalDebits: number;
  originalLoan?: number;
  loanBalance?: number;
  amountPaid?: number;
  loanStatus?: string;
  status: string;
  generatedDate: string;
  transactions?: Transaction[];
  roiHistory?: ROIHistory[];
}

const Statements: React.FC = () => {
  const user = useDecodedToken();
  const [statements, setStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState<string>('all');

  useEffect(() => {
    const fetchStatements = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get account number from user profile
        const accountNumber = user?.accountNumber || '';
        if (!accountNumber) {
          setError('Account number not found');
          return;
        }

        // Fetch client accounts exactly like Dashboard does
        const accRes = await api.get(`/api/clients/${accountNumber}/accounts`);
        const loanAccounts = accRes.data.loanAccounts || [];
        
        const detailedStatements: Statement[] = [];
        
        // Fetch detailed loan data for each loan account
        for (const loanAccount of loanAccounts) {
          try {
            // Get detailed loan data with transactions (same as loan details)
            const loanRes = await api.get(`/api/loans/${loanAccount.id}/associations?associations=transactions`);
            const loan = loanRes.data;
            const transactions = loan.transactions || [];
            
            // Transform data exactly like loan details does
            const transformedData = {
              borrowerName: loan.clientName || 'N/A',
              coApplicantName: '',
              address: '',
              loanNumber: loan.accountNo || loan.loanNumber || 'N/A',
              disbursedAmount: loan.summary?.principalDisbursed || loan.disbursedAmount || 0,
              termOfLoan: loan.termFrequency || 0,
              termUnit: loan.termPeriodFrequencyType?.value || 'Months',
              rateOfInterest: loan.annualInterestRate || loan.interestRatePerPeriod || 0,
              emiStartDate: '',
              periodFrom: loan.timeline?.submittedOnDate ? 
                new Date(loan.timeline.submittedOnDate[0], loan.timeline.submittedOnDate[1] - 1, loan.timeline.submittedOnDate[2]).toLocaleDateString() : '',
              periodTo: loan.timeline?.expectedMaturityDate ? 
                new Date(loan.timeline.expectedMaturityDate[0], loan.timeline.expectedMaturityDate[1] - 1, loan.timeline.expectedMaturityDate[2]).toLocaleDateString() : '',
              transactions: (() => {
                // Map and categorize to debit/credit exactly like loan details
                const baseRows = transactions.map((tx: any, index: number) => {
                  const date = tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : '';
                  const type = tx.type?.value || '';
                  const amount = tx.amount || 0;
                  const balance = tx.outstandingLoanBalance || 0;
                  
                  let debit = 0;
                  let credit = 0;
                  
                  if (type.toLowerCase().includes('repayment') || type.toLowerCase().includes('credit')) {
                    credit = amount;
                  } else {
                    debit = amount;
                  }
                  
                  return {
                    txnDate: date,
                    voucher: tx.id || '',
                    narration: type,
                    debit: debit,
                    credit: credit,
                    balance: balance
                  };
                });
                
                return baseRows;
              })(),
              roiHistory: [{
                sno: 1,
                startDate: loan.timeline?.submittedOnDate ? 
                  new Date(loan.timeline.submittedOnDate[0], loan.timeline.submittedOnDate[1] - 1, loan.timeline.submittedOnDate[2]).toLocaleDateString() : '',
                endDate: loan.timeline?.expectedMaturityDate ? 
                  new Date(loan.timeline.expectedMaturityDate[0], loan.timeline.expectedMaturityDate[1] - 1, loan.timeline.expectedMaturityDate[2]).toLocaleDateString() : '',
                roi: `${loan.annualInterestRate || 0}% PA`,
                emiAmount: 0
              }],
              pageInfo: {
                currentPage: 1,
                totalPages: 1,
                takenBy: user?.username || 'System',
                dateTime: new Date().toLocaleString()
              }
            };

            // Create statement object
            const statement: Statement = {
              id: loan.id || loanAccount.id,
              accountNo: accountNumber,
              loanAccountNo: loan.accountNo || loanAccount.accountNo,
              accountType: 'Loan',
              productName: loan.loanProductName || loanAccount.productName,
              shortProductName: loanAccount.shortProductName,
              period: `${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
              startDate: transformedData.periodFrom,
              endDate: transformedData.periodTo,
              openingBalance: 0,
              closingBalance: loan.summary?.principalOutstanding || 0,
              totalCredits: 0,
              totalDebits: 0,
              originalLoan: transformedData.disbursedAmount,
              loanBalance: loan.summary?.principalOutstanding || 0,
              amountPaid: loan.summary?.principalPaid || 0,
              loanStatus: loan.status?.value || 'Unknown',
              status: 'Generated',
              generatedDate: new Date().toISOString().slice(0,10),
              transactions: transformedData.transactions,
              roiHistory: transformedData.roiHistory
            };

            detailedStatements.push(statement);
          } catch (loanErr: any) {
            console.error(`Failed to fetch detailed data for loan ${loanAccount.id}:`, loanErr);
          }
        }
        
        setStatements(detailedStatements);
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load statements';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchStatements();
  }, [selectedAccountType, user?.accountNumber]);

  const filteredStatements = statements.filter(
    statement => 
      (!search || 
        statement.accountNo.toLowerCase().includes(search.toLowerCase()) ||
        statement.period.toLowerCase().includes(search.toLowerCase())) &&
      (selectedAccountType === 'all' || statement.accountType.toLowerCase() === selectedAccountType.toLowerCase())
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'generated': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const handleDownload = async (statement: Statement, format: 'pdf' | 'excel') => {
    try {
      
      // Create a filename
      const filename = format === 'excel' 
        ? `statement_${statement.id}_${statement.period.replace(' ', '_')}.xlsx`
        : `statement_${statement.id}_${statement.period.replace(' ', '_')}.pdf`;
      
      if (format === 'excel') {
        try {
          // Create workbook using static import
          const workbook = new ExcelJS.Workbook();

        
          // Statement Summary Sheet
          const summarySheet = workbook.addWorksheet('Statement Summary');
          
          // Add title
          summarySheet.addRow(['SMEDCO LOAN STATEMENT']);
          summarySheet.addRow(['']);
          summarySheet.addRow(['Statement Summary']);
          summarySheet.addRow(['Field', 'Value']);
          summarySheet.addRow(['Loan ID', statement.id]);
          summarySheet.addRow(['Loan Account', statement.loanAccountNo || statement.accountNo]);
          summarySheet.addRow(['Product', statement.productName || 'N/A']);
          summarySheet.addRow(['Period', statement.period]);
          summarySheet.addRow(['Original Loan', `$${(statement.originalLoan || 0).toFixed(2)}`]);
          summarySheet.addRow(['Current Balance', `$${(statement.loanBalance || 0).toFixed(2)}`]);
          summarySheet.addRow(['Amount Paid', `$${(statement.amountPaid || 0).toFixed(2)}`]);
          summarySheet.addRow(['Status', statement.loanStatus || 'Unknown']);
          summarySheet.addRow(['Generated', statement.generatedDate]);
          summarySheet.addRow(['']);
          summarySheet.addRow(['Generated By: SMEDCO System']);
          summarySheet.addRow(['Date & Time: ' + new Date().toLocaleString()]);
          
          // Transaction History Sheet (if available)
          if (statement.transactions && statement.transactions.length > 0) {
            const transactionSheet = workbook.addWorksheet('Transactions');
            
            transactionSheet.addRow(['Transaction History']);
            transactionSheet.addRow(['']);
            transactionSheet.addRow(['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance']);
            
            statement.transactions.forEach(tx => {
              transactionSheet.addRow([
                tx.txnDate,
                tx.voucher,
                tx.narration,
                tx.debit > 0 ? `$${tx.debit.toFixed(2)}` : '-',
                tx.credit > 0 ? `$${tx.credit.toFixed(2)}` : '-',
                `$${tx.balance.toFixed(2)}`
              ]);
            });
          }
          
          // ROI History Sheet (if available)
          if (statement.roiHistory && statement.roiHistory.length > 0) {
            const roiSheet = workbook.addWorksheet('ROI History');
            
            roiSheet.addRow(['Rate of Interest History']);
            roiSheet.addRow(['']);
            roiSheet.addRow(['S.No', 'Start Date', 'End Date', 'ROI', 'EMI Amount']);
            
            statement.roiHistory.forEach(roi => {
              roiSheet.addRow([
                roi.sno,
                roi.startDate,
                roi.endDate,
                roi.roi,
                `$${roi.emiAmount.toFixed(2)}`
              ]);
            });
          }
          
          // Write and download the Excel file

          const buffer = await workbook.xlsx.writeBuffer();

          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);

          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

        } catch (excelError) {
          console.error('Excel generation error:', excelError);

          
          // Fallback to CSV if Excel fails
          const csvContent = [
            ['Field', 'Value'],
            ['Loan ID', statement.id],
            ['Loan Account', statement.loanAccountNo || statement.accountNo],
            ['Product', statement.productName || 'N/A'],
            ['Period', statement.period],
            ['Original Loan', `$${(statement.originalLoan || 0).toFixed(2)}`],
            ['Current Balance', `$${(statement.loanBalance || 0).toFixed(2)}`],
            ['Amount Paid', `$${(statement.amountPaid || 0).toFixed(2)}`],
            ['Status', statement.loanStatus || 'Unknown'],
            ['Generated', statement.generatedDate]
          ].map(row => row.join(',')).join('\n');
          
          // Create and download the CSV file
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename.replace('.xlsx', '.csv'));
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error('Error downloading statement:', error);
      alert('Error downloading statement. Please try again.');
    }
  };

  return (
    <FeatureGuard feature="statements">
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Account Statements
            </Typography>
          </Box>
          <UserTypeIndicator />
        </Box>

        {/* Access Level Information */}
        {!user?.accountNumber && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>No linked account yet:</strong> Statements are only available once your SMEDCO account number is verified. 
              You can still browse and submit loan applications from the Loan Applications page. Link your account later to view statements.
            </Typography>
          </Alert>
        )}

        {/* Search and Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <TextField
            placeholder="Search statements..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Account Type"
            value={selectedAccountType}
            onChange={e => setSelectedAccountType(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
            <MenuItem value="loan">Loan</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
          >
            More Filters
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                      {statements.length}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Statements
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                      {statements.filter(s => s.status === 'Generated').length}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Generated
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                      {statements.filter(s => s.accountType === 'Savings').length}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Savings Accounts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                      {statements.filter(s => s.accountType === 'Loan').length}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Loan Accounts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Statements Table */}
            <TableContainer component={Paper} elevation={2}>
              <Table>
            <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Product</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Period</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Original Loan</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Current Balance</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount Paid</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Generated</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
                <TableBody>
                  {filteredStatements.map((statement) => (
                    <TableRow key={statement.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {statement.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Account: {statement.loanAccountNo || statement.accountNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {statement.shortProductName || statement.productName || 'N/A'}
                          </Typography>
                          {statement.productName && statement.shortProductName && (
                            <Typography variant="caption" color="text.secondary">
                              {statement.productName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{statement.period}</TableCell>
                      <TableCell>{formatCurrency(statement.originalLoan || 0)}</TableCell>
                      <TableCell>{formatCurrency(statement.loanBalance || 0)}</TableCell>
                      <TableCell>{formatCurrency(statement.amountPaid || 0)}</TableCell>
                      <TableCell>
                        <Chip
                          label={statement.loanStatus || 'Unknown'}
                          color={getStatusColor(statement.loanStatus || 'Unknown') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(statement.generatedDate)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <PDFDownloadLink
                            document={<StatementPdf statement={statement} />}
                            fileName={`statement_${statement.id}_${statement.period.replace(' ', '_')}.pdf`}
                          >
                            {({ blob, url, loading, error }) => (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                disabled={loading}
                              >
                                {loading ? 'Generating...' : error ? 'Error' : 'PDF'}
                              </Button>
                            )}
                          </PDFDownloadLink>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(statement, 'excel')}
                          >
                            Excel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredStatements.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Statements Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {user?.accountNumber
                    ? (search || selectedAccountType !== 'all' 
                        ? 'Try adjusting your search or filters to find statements.' 
                        : 'You don\'t have any statements yet. They will appear here once generated.')
                    : 'Link your SMEDCO account number to view statements. You can still manage loan applications now.'}
                </Typography>
                <Box>
                  <Button variant="contained" href="/loan-application" sx={{ mr: 1 }}>
                    Go to Loan Applications
                  </Button>
                  {!user?.accountNumber && (
                    <Button variant="outlined" href="/profile">
                      Verify Account Number
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </FeatureGuard>
  );
};

export default Statements;



